import { useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { Scale, Send, BookOpen, Sparkles, FileText, Paperclip, Trash2 } from "lucide-react";
import { api } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

const SUGGESTIONS = [
  "Qual a duração mínima de um contrato habitacional?",
  "Como funciona a atualização anual da renda?",
  "O que acontece se o inquilino não pagar a renda?",
  "A caução pode ser retida no fim do contrato?",
];

const LEGAL_CONTRACT_CATEGORY = "legal_contract";

export default function Legal() {
  const { t } = useI18n();
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const { data: documents = [] } = useQuery({
    queryKey: ["documents"],
    queryFn: async () => (await api.get("/documents")).data,
  });
  const myContracts = documents.filter((d) => d.category === LEGAL_CONTRACT_CATEGORY);

  const removeContract = useMutation({
    mutationFn: (id) => api.delete(`/documents/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["documents"] }),
  });

  const uploadContract = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("category", LEGAL_CONTRACT_CATEGORY);
      await api.post("/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      qc.invalidateQueries({ queryKey: ["documents"] });
      toast.success("OK");
    } catch (err) {
      toast.error("Upload error");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const ask = async (question) => {
    const text = (question || q).trim();
    if (!text) return;
    setLoading(true);
    setResult(null);
    try {
      const res = await api.post("/legal/ask", { question: text });
      setResult(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6 max-w-3xl" data-testid="legal-page">
      <div className="flex items-center gap-3">
        <div className="h-11 w-11 rounded-sm bg-secondary flex items-center justify-center">
          <Scale className="h-5 w-5 text-[hsl(var(--success))]" />
        </div>
        <div>
          <h1 className="font-display text-4xl">{t("legal_title")}</h1>
          <p className="text-sm text-muted-foreground mt-1">{t("legal_sub")}</p>
        </div>
      </div>

      <Card className="p-6 rounded-sm border-border space-y-3" data-testid="my-contracts-card">
        <div>
          <h3 className="font-medium flex items-center gap-2"><FileText className="h-4 w-4" /> {t("my_contracts_title")}</h3>
          <p className="text-xs text-muted-foreground mt-1">{t("my_contracts_desc")}</p>
        </div>
        <input ref={fileRef} type="file" accept=".pdf" hidden data-testid="my-contract-file-input" onChange={uploadContract} />
        <Button type="button" variant="outline" size="sm" onClick={() => fileRef.current?.click()}
          disabled={uploading} data-testid="my-contract-upload-btn" className="rounded-sm gap-2">
          <Paperclip className="h-4 w-4" /> {uploading ? "..." : t("my_contracts_upload")}
        </Button>
        {myContracts.length > 0 && (
          <div className="flex flex-wrap gap-2 pt-1">
            {myContracts.map((d) => (
              <Badge key={d.id} variant="secondary" className="rounded-sm gap-2 pr-1" data-testid={`my-contract-${d.id}`}>
                {d.original_filename}
                <button onClick={() => removeContract.mutate(d.id)} data-testid={`my-contract-remove-${d.id}`}
                  className="hover:text-[hsl(var(--destructive))]"><Trash2 className="h-3 w-3" /></button>
              </Badge>
            ))}
          </div>
        )}
      </Card>

      <Card className="p-6 rounded-sm border-border space-y-4">
        <Textarea
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={t("ask_placeholder")}
          data-testid="legal-input"
          className="rounded-sm min-h-24"
        />
        <div className="flex flex-wrap gap-2">
          {SUGGESTIONS.map((s) => (
            <button key={s} onClick={() => { setQ(s); ask(s); }}
              className="text-xs px-3 py-1.5 rounded-full border border-border text-muted-foreground hover:text-foreground hover:border-foreground transition-colors">
              {s}
            </button>
          ))}
        </div>
        <Button onClick={() => ask()} disabled={loading} data-testid="legal-ask-btn" className="rounded-sm gap-2">
          <Send className="h-4 w-4" /> {loading ? t("analyzing") : t("ask_btn")}
        </Button>
      </Card>

      {result && (
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
          <Card className="p-6 rounded-sm border-border" data-testid="legal-answer">
            <div className="flex items-center gap-2 mb-4 text-[hsl(var(--success))]">
              <Sparkles className="h-4 w-4" /><span className="text-xs uppercase tracking-wider">YieldPilot AI</span>
            </div>
            <p className="text-sm whitespace-pre-wrap leading-relaxed">{result.answer}</p>
            {result.ai_generated === false && (
              <p className="text-xs text-muted-foreground mt-4" data-testid="legal-ai-fallback-note">{t("ai_fallback_note")}</p>
            )}
            {result.sources?.length > 0 && (
              <div className="mt-6 pt-4 border-t border-border">
                <div className="flex items-center gap-2 mb-3 text-muted-foreground">
                  <BookOpen className="h-4 w-4" /><span className="text-xs uppercase tracking-wider">{t("sources")}</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {result.sources.map((s) => (
                    <Badge key={s.id} variant="secondary" className="rounded-sm" data-testid={`source-${s.id}`}>{s.topic}</Badge>
                  ))}
                </div>
              </div>
            )}
          </Card>
        </motion.div>
      )}
    </div>
  );
}
